/**
* @Author: Nemo
* @Description: 表单字段组件 - 专注于根据配置动态渲染不同类型的表单控件
* @Date: 2024-12-19
* @FilePath: /rhcn-checkout-web/src/componentsV2/busi/dynamic-form/FormField.vue
*/
<template>
  <div class="form-field">
    <!-- 动态渲染组件，并监听相关事件 -->
    <component :is="resolvedComponent" v-bind="fieldProps" :value="value"
      :validation-mode="$parent.internalValidationMode || 'format-only'" @input="onInput" @focus="onFocus"
      @blur="onBlur" @change="onChange" @validate="onValidate" @update:cardNum="onCardInfoUpdate('cardNum', $event)"
      @update:effectiveDate="onCardInfoUpdate('effectiveDate', $event)" @update:cvv="onCardInfoUpdate('cvv', $event)">
      <!-- 支持suffix插槽 - 开发者：Nemo -->
      <template v-if="field.suffix" #suffix>
        <div class="field-suffix-wrapper">
          <!-- 如果suffix是对象配置 -->
          <img v-if="field.suffix.type === 'image'" :src="field.suffix.src" :alt="field.suffix.alt || ''"
            :style="field.suffix.style" class="suffix-image" />
          <!-- 如果suffix是字符串（兼容旧方式） -->
          <div v-else-if="typeof field.suffix === 'string'" v-html="field.suffix"></div>
        </div>
      </template>
    </component>

    <!-- 分期提示 - 只针对分期字段且选择了非全额付款时显示 -->
    <div v-if="showInstallmentTip" class="installment-tip">
      {{ $t('message.checkout.intallmentTip') }}
    </div>

    <!-- 加载状态覆盖层 -->
    <div v-if="field.loadingState" class="field-loading-overlay">
      <div class="loading-container" v-loading="true" element-loading-background="transparent"></div>
    </div>
  </div>
</template>

<script>
// 导入基础组件
import BaseAutocomplete from '@/componentsV2/base/autocomplete';
import BaseInput from '@/componentsV2/base/input';
import BaseSelect from '@/componentsV2/base/select';
import BaseTextarea from '@/componentsV2/base/textarea';
import CardInformationForm from '@/componentsV2/busi/card-information-form';
import CountrySelect from '@/componentsV2/busi/country-select';
export default {
  name: 'FormField',

  components: {
    BaseInput,
    BaseSelect,
    CountrySelect,
    CardInformationForm,
    BaseAutocomplete,
    BaseTextarea,
  },

  props: {
    // 字段配置对象
    field: {
      type: Object,
      required: true,
    },

    // 字段值
    value: {
      type: [String, Number, Boolean, Array, Object],
      default: '',
    },

    // 错误状态
    error: {
      type: Boolean,
      default: false,
    },

    // 额外属性，传递给特定类型的组件
    extraProps: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    // 解析实际要渲染的组件
    resolvedComponent() {
      // 特殊处理卡信息组件
      if (this.field.renderComponent === 'CardInformationForm' ||
        this.field.renderComponent === 'card-information' ||
        this.field.componentType === 'card-information') {
        return 'CardInformationForm';
      }
      // 从组件注册表中获取组件
      return this.field.renderComponent || 'BaseInput';
    },

    // 是否显示分期提示
    showInstallmentTip() {
      // 只针对分期字段
      if (this.field.name !== 'periodValue') return false;

      // 只有当选择了分期且不是空值或全额付款时才显示
      return this.value && !['', '1'].includes(this.value);
    },

    // 构建传递给组件的props
    fieldProps() {
      const { name } = this.field;

      // 基础属性
      const props = {
        placeholder: this.getPlaceholder(),
        name,
        error: !!this.error,
        disabled: this.field.disabled,
        options: this.field.options,
      };

      // 添加最大长度限制
      if (this.field.maxLength) {
        props.maxLength = this.field.maxLength;
      }

      // 添加inputHandler支持
      if (this.field.inputHandler && this.$parent && this.$parent.$parent) {
        const parentComponent = this.$parent.$parent;
        if (typeof parentComponent[this.field.inputHandler] === 'function') {
          props.inputHandler = parentComponent[this.field.inputHandler];
        }
      }

      // 传递通用字段属性
      if (this.field.resizable !== undefined) {
        props.resizable = this.field.resizable;
      }
      if (this.field.rows !== undefined) {
        props.rows = this.field.rows;
      }
      if (this.field.customStyle) {
        props.customStyle = this.field.customStyle;
      }

      // 根据组件类型添加特定属性
      if (this.resolvedComponent === 'BaseInput') {
        props.type = this.getInputType();
      }

      // 为卡信息组件设置特定属性
      if (this.resolvedComponent === 'CardInformationForm' || this.resolvedComponent === 'card-information') {
        props.rules = this.field.validationRules || [];

        // 卡信息组件特殊处理：传递所有必要的字段值
        if (this.$parent && this.$parent.formData) {
          props.cardNum = this.value; // cardNum就是当前字段的值
          props.effectiveDate = this.$parent.formData.effectiveDate || '';
          props.cvv = this.$parent.formData.cvv || '';
        }

        // 添加 cardTypeInfo 属性
        if (this.extraProps && this.extraProps.cardTypeInfo) {
          props.cardTypeInfo = this.extraProps.cardTypeInfo;
        }
      }

      return props;
    },
  },

  methods: {
    // 获取输入框类型
    getInputType() {
      // 如果字段有明确指定inputType，优先使用
      if (this.field.inputType) {
        return this.field.inputType;
      }

      return 'text';
    },

    // 获取占位符文本
    getPlaceholder() {
      // 如果字段有明确指定placeholder，优先使用
      if (this.field.placeholder) {
        // 处理多语言键
        if (typeof this.field.placeholder === 'string' && this.field.placeholder.startsWith('message.')) {
          return this.$t(this.field.placeholder);
        }
        return this.field.placeholder;
      }

      // 如果字段有明确指定label，则用label作为占位符
      if (this.field.label) {
        // 处理多语言键
        if (typeof this.field.label === 'string' && this.field.label.startsWith('message.')) {
          return this.$t(this.field.label);
        }
        return this.field.label;
      }

      return this.field.name;
    },

    // 输入事件处理
    onInput(val) {
      this.$emit('input', val);
    },

    // 聚焦事件处理
    onFocus() {
      this.$emit('focus', {
        name: this.field.name,
        value: this.value,
      });
    },

    // 失焦事件处理
    onBlur() {
      const data = {
        name: this.field.name,
        value: this.value,
      };

      this.$emit('blur', data);

      // 在失焦时同时触发change事件，以符合HTML表单标准行为
      // this.$emit('change', data);
    },

    // 变更事件处理
    onChange() {
      this.$emit('change', {
        name: this.field.name,
        value: this.value,
      });
    },

    // 验证事件处理，用于处理复杂组件（如卡信息组件）的验证结果
    onValidate(result) {
      this.$emit('validate', {
        name: this.field.name,
        ...result,
      });
    },

    /**
     * 卡信息组件特殊处理：
     * 处理卡信息组件的effectiveDate和cvv字段更新
     * 由于卡信息组件是一个特殊组件，包含多个字段值，
     * 需要单独处理以实现双向绑定
     */
    onCardInfoUpdate(fieldName, value) {
      // 只在渲染卡信息组件时处理
      if (this.resolvedComponent === 'card-information' ||
        this.resolvedComponent === 'CardInformationForm') {
        // 通过父组件更新关联字段
        if (this.$parent && this.$parent.formData) {
          this.$parent.$set(this.$parent.formData, fieldName, value);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-field {
  width: 100%;
  position: relative;

  .form-component {
    width: 100%;
  }

  /* 分期提示样式 */
  .installment-tip {
    margin-top: 6px;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #8D98AF;
    font-family: 'Open Sans regular'
  }

  /* Suffix样式 - 开发者：Nemo */
  .field-suffix-wrapper {
    display: flex;
    height: 100%;
    align-items: center;

    .suffix-image {
      max-width: 100%;
      height: auto;
    }
  }

  /* 加载状态覆盖层 */
  .field-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    z-index: 10;

    .loading-container {
      width: 100%;
      height: 100%;

      /* 修改Element UI的loading样式 */
      ::v-deep .el-loading-mask {
        background-color: transparent;
      }

      ::v-deep .el-loading-spinner {
        top: 50% !important;
        margin-top: 0 !important;
        transform: translateY(-50%) !important;
      }

      ::v-deep .circular {
        width: 20px !important;
        height: 20px !important;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
